// ChatSessionProvider — bootstraps the shared chat workspace stores once
// capabilities/auth have resolved. Keeps login-scoped shared state and
// tab-local view state aligned with the authenticated session.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useCapabilities } from './CapabilitiesProvider';
import {
  useChatLockStore,
  useChatViewStore,
  useChatWorkspaceStore,
} from '@/shared/chat-workspace';
import { clearAgenthubRuntimeSessions } from '@/shared/chat-runtime';

type Props = { children: ReactNode };

export function ChatSessionProvider({ children }: Props) {
  const { capabilities, loading, status } = useCapabilities();
  const resetGeneration = useChatWorkspaceStore((state) => state.resetGeneration);
  const userId = capabilities?.user_id;
  // `auth_session_id` is only populated behind a real BFF session cookie
  // (OAuth); dev-local auth has no such cookie and always returns null.
  // Falling back to a fixed scope for that case (rather than treating it as
  // "not logged in" and clearing chat-view state on every capabilities
  // refetch) keeps per-agent thread resume working in local dev, matching
  // production behavior where auth_session_id is always present.
  const loginSessionId = capabilities?.auth_session_id ?? (userId ? 'dev-local' : null);
  const scopeResolved = !loading && status !== 'error';
  const nextRuntimeScope = status === 'unauthenticated' || !userId || !loginSessionId
    ? null
    : JSON.stringify([userId, loginSessionId]);
  const [configuredRuntimeScope, setConfiguredRuntimeScope] = useState<string | null>();
  const observedResetGenerationRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!scopeResolved || configuredRuntimeScope === nextRuntimeScope) return;
    clearAgenthubRuntimeSessions();
    if (status === 'unauthenticated' || !userId || !loginSessionId) {
      useChatLockStore.getState().clearSession();
      useChatViewStore.getState().clearSession();
      useChatWorkspaceStore.getState().clearSession();
      observedResetGenerationRef.current = useChatWorkspaceStore.getState().resetGeneration;
      setConfiguredRuntimeScope(null);
      return;
    }

    const scope = { userId, loginSessionId };
    useChatWorkspaceStore.getState().configureSession(scope);
    useChatViewStore.getState().configureSession(scope);
    useChatLockStore.getState().configureSession(scope);
    observedResetGenerationRef.current = useChatWorkspaceStore.getState().resetGeneration;
    setConfiguredRuntimeScope(nextRuntimeScope);
  }, [configuredRuntimeScope, loginSessionId, nextRuntimeScope, scopeResolved, status, userId]);

  useEffect(() => {
    if (
      !scopeResolved
      || configuredRuntimeScope !== nextRuntimeScope
      || observedResetGenerationRef.current === resetGeneration
    ) return;
    observedResetGenerationRef.current = resetGeneration;
    clearAgenthubRuntimeSessions();
    useChatViewStore.getState().applyResetGeneration(resetGeneration);
  }, [configuredRuntimeScope, nextRuntimeScope, resetGeneration, scopeResolved]);

  // Children must not mount until capabilities have resolved AND the
  // workspace stores are configured for that identity. Mounting speculatively
  // while /auth/me is still in flight (i.e. gating only on the "resolved but
  // mismatched" case) let ChatWorkspace start an active-run recovery fetch
  // before scope was known, and then torn that fetch down moments later when
  // capabilities resolved and this gate flipped to unmount-then-remount —
  // silently aborting an in-flight stream reattach with no visible error.
  // Waiting for scopeResolved up front makes the mount atomic: one mount per
  // page load, always against an already-configured scope.
  if (!scopeResolved || configuredRuntimeScope !== nextRuntimeScope) return null;

  return <>{children}</>;
}
